import { DevelopmentError } from '../../../webmodule-common/other/development-error';
import type { AssetVirtualPathInfo, AttachedBlobAsset } from '../../api/dealer-api-interface-quote';

const DOCUMENT_PREFIX = './document/';

type DocumentAssetMap = { [key: string]: AssetVirtualPathInfo } | null | undefined;

type ParsedDocumentSegments = Omit<ParsedDocumentPath, 'asset' | 'assetKey'>;

export interface InlineDocumentAsset {
  origin: string;
  name: string;
  contentType: string;
  content: string;
  hash: string;
}

export interface ParsedDocumentPath {
  origin: string;
  docType: string;
  name: string;
  asset: AssetVirtualPathInfo;
  assetKey: string;
}

const parseDocumentPath = (path?: string | null): ParsedDocumentSegments | null => {
  if (!path || !path.startsWith(DOCUMENT_PREFIX)) return null;

  const remainder = path.slice(DOCUMENT_PREFIX.length);
  const segments = remainder.split('/');
  if (segments.length < 3) return null;

  const origin = segments[0]?.trim();
  const docType = segments[1]?.trim();
  const filename = segments.slice(2).join('/').trim();

  if (!origin || !docType || !filename) return null;

  const lastDot = filename.lastIndexOf('.');
  const name = lastDot > 0 ? filename.slice(0, lastDot) : filename;

  return {
    origin,
    docType,
    name
  };
};

/**
 * Extract document metadata (origin/docType/name + asset ref) from assetVirtualPaths entries that point at document paths.
 */
export function extractDocumentDescriptors(assetVirtualPaths: DocumentAssetMap): ParsedDocumentPath[] {
  if (!assetVirtualPaths) return [];

  const seen = new Set<string>();

  return Object.entries(assetVirtualPaths).reduce<ParsedDocumentPath[]>((docs, [assetKey, asset]) => {
    if (!asset) return docs;
    const key = asset.path ?? '';
    if (!key || seen.has(key)) return docs;

    const parsed = parseDocumentPath(key);
    if (!parsed) return docs;

    seen.add(key);
    docs.push({
      origin: parsed.origin,
      docType: parsed.docType,
      name: parsed.name,
      asset,
      assetKey
    });
    return docs;
  }, []);
}

function createDocumentAssetKey(origin: string, contentType: string, name: string): string {
  if (!name || name.trim() === '') {
    name = 'default';
  }
  return `document/${origin}/${contentType}/${name}`;
}
function createAssetDocumentPath(origin: string, docType: string, name: string, ext: string): string {
  const extension = ext.startsWith('.') ? ext : `.${ext}`;
  const documentName = !name ? 'default' : name;
  return `./document/${origin}/${docType}/${documentName}${extension}`;
}

const getWebCrypto = (): Crypto => {
  const cryptoApi = globalThis?.crypto;
  if (!cryptoApi?.subtle) throw new DevelopmentError('WebCrypto API is not available in this environment');
  return cryptoApi;
};

const toArrayBuffer = (bytes: Uint8Array): ArrayBuffer => {
  const buffer = bytes.buffer;
  if (buffer instanceof ArrayBuffer) {
    if (bytes.byteOffset === 0 && bytes.byteLength === buffer.byteLength) return buffer;
    return buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);
  }
  // SharedArrayBuffer or other views: copy into a new ArrayBuffer to satisfy WebCrypto
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
};

// Browser/WebCrypto variant
export async function computeHashBase64Web(data: Uint8Array | string): Promise<string> {
  const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : data;
  if (!bytes || bytes.length === 0) return '';

  const digest = await getWebCrypto().subtle.digest('SHA-256', toArrayBuffer(bytes));
  const hashBytes = new Uint8Array(digest);

  // Convert to Base64
  let binary = '';
  for (let i = 0; i < hashBytes.byteLength; i++) {
    binary += String.fromCharCode(hashBytes[i]);
  }
  return btoa(binary); // standard Base64 encoding; matches .NET Convert.ToBase64String
}

export function addDocumentAssetsFromOrigin(
  assets: DocumentAssetMap,
  newDocs: InlineDocumentAsset[],
  originsToDelete?: string[]
): { [key: string]: AttachedBlobAsset } {
  const result: { [key: string]: AttachedBlobAsset } = {};
  const existing = extractDocumentDescriptors(assets);

  // first we set all items to deleted that exist in the origins. now we merge insert.
  originsToDelete?.forEach(origin => {
    existing
      .filter(doc => doc.origin === origin)
      .forEach(doc => {
        result[doc.assetKey] = { delete: true, assetPath: doc.asset.path, data: null, dataBase64: null, hasContent: false};
      });
  });

  newDocs.forEach(doc => {
    const docAssetKey = createDocumentAssetKey(doc.origin, doc.contentType, doc.name);
    const assetPath = createAssetDocumentPath(doc.origin, doc.contentType, doc.name, '.md');

    const existingAsset = existing.find(
      d => d.origin === doc.origin && d.docType === doc.contentType && d.name === doc.name
    );
    if (existingAsset) {
      if (existingAsset.asset.hash === doc.hash) {
        delete result[docAssetKey]; // no change
        return;
      }
    }

    let asset = result[docAssetKey];
    if (!asset) {
      asset = { delete: false, assetPath: assetPath, data: doc.content ? doc.content : null, dataBase64: null, hasContent: false };
      result[createDocumentAssetKey(doc.origin, doc.contentType, doc.name)] = asset;
    } else {
      asset.delete = false;
      asset.data = doc.content ? doc.content : null;
      asset.assetPath = assetPath;
    }
  });
  return result;
}
